import { useEffect, useRef, useState } from 'react';
import type { ColumnSizingState, OnChangeFn } from '@/lib/tanstack-table';
import type { ControlledTableState } from '@/components/common/data-table/use-data-table';
import {
  activeLevels,
  activeSortLimit,
  withInactiveLevels,
} from '@/components/common/columnConfigSorting';
import { columnSizing } from '../utils/buyerBoardColumns';
import type { BuyerBoardColumnConfig } from './useBuyerBoardColumnConfig';

// A resize drag streams widths on every pointer move; save once it settles.
const SAVE_WIDTHS_AFTER_MS = 400;

function resolve<T>(updater: T | ((old: T) => T), current: T): T {
  return typeof updater === 'function'
    ? (updater as (old: T) => T)(current)
    : updater;
}

/**
 * The line table's sort, column order and widths, kept in the saved view
 * rather than the table: a header click or resize saves to the buyer's view.
 * The order follows the view; columns are reordered in the Columns dialog,
 * not by dragging headers. Only the drawn columns with Sort on sort; levels
 * on the others stay saved for when they come back.
 */
export function useBuyerBoardTableState(
  config: BuyerBoardColumnConfig,
  renderedIds: readonly string[],
): ControlledTableState {
  const unsortable = new Set(
    config.columns.flatMap((column) =>
      column.sortable === false ? [column.id] : [],
    ),
  );
  const active = new Set(renderedIds.filter((id) => !unsortable.has(id)));
  const sorting = activeLevels(config.sorting, active);
  const savedSizing = columnSizing(config.columns);
  const [draftSizing, setDraftSizing] = useState<ColumnSizingState | null>(
    null,
  );
  const sizing = draftSizing ?? savedSizing;

  const saveWidthsRef = useRef(config.setWidths);
  useEffect(() => {
    saveWidthsRef.current = config.setWidths;
  });
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const unsavedWidthsRef = useRef<ColumnSizingState | null>(null);
  // Leaving the board mid-wait still saves the last resize.
  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      if (unsavedWidthsRef.current)
        saveWidthsRef.current(unsavedWidthsRef.current);
    },
    [],
  );

  // Without a table-level maxMultiSortColCount a shift-click past the limit
  // comes back one level longer, so it can be refused here; with one, the
  // table would drop the first level to make room.
  const onSortingChange: ControlledTableState['onSortingChange'] = (
    updater,
  ) => {
    const next = resolve(updater, sorting);
    if (next.length > activeSortLimit(config.sorting, active)) return;
    config.setSorting(withInactiveLevels(next, config.sorting, active));
  };

  const onColumnSizingChange: OnChangeFn<ColumnSizingState> = (updater) => {
    const next = resolve(updater, sizing);
    setDraftSizing(next);
    unsavedWidthsRef.current = next;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      unsavedWidthsRef.current = null;
      saveWidthsRef.current(next);
      setDraftSizing(null);
    }, SAVE_WIDTHS_AFTER_MS);
  };

  return {
    sorting,
    onSortingChange,
    columnOrder: [...renderedIds],
    columnSizing: sizing,
    onColumnSizingChange,
  };
}
